import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster, toast as sonnerToast } from 'sonner';
import { getMe } from '@/lib/api';
import { useOffline } from '@/lib/offline';
import Layout from '@/components/Layout';
import LoginPage from '@/pages/LoginPage';
import DashboardPage from '@/pages/DashboardPage';
import InventoryPage from '@/pages/InventoryPage';
import PackPage from '@/pages/PackPage';
import ScanPage from '@/pages/ScanPage';
import PalletDetailPage from '@/pages/PalletDetailPage';
import WarehouseMapPage from '@/pages/WarehouseMapPage';
import IssuePage from '@/pages/IssuePage';
import AdminPage from '@/pages/AdminPage';
import ReportsPage from '@/pages/ReportsPage';
import CustomersPage from '@/pages/CustomersPage';
import GRNPage from '@/pages/GRNPage';
import LabelsPage from '@/pages/LabelsPage';
import CycleCountPage from '@/pages/CycleCountPage';
import ReturnsPage from '@/pages/ReturnsPage';
import ToolStorePage from '@/pages/ToolStorePage';
import ToolDetailPage from '@/pages/ToolDetailPage';
import '@/App.css';

export const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastActivity, setLastActivity] = useState(Date.now());

  useEffect(() => {
    const token = localStorage.getItem('wms_token');
    const savedUser = localStorage.getItem('wms_user');
    if (token && savedUser) {
      try {
        setUser(JSON.parse(savedUser));
      } catch (err) { console.error('Failed to parse saved user data:', err); localStorage.removeItem('wms_user'); }
    }
    setLoading(false);
  }, []);

  const handleLogin = useCallback((token, userData) => {
    localStorage.setItem('wms_token', token);
    localStorage.setItem('wms_user', JSON.stringify(userData));
    setUser(userData);
    setLastActivity(Date.now());
  }, []);

  const handleLogout = useCallback(() => {
    localStorage.removeItem('wms_token');
    localStorage.removeItem('wms_user');
    setUser(null);
  }, []);

  // Idle timeout - 2 hours
  useEffect(() => {
    if (!user) return;
    const resetActivity = () => setLastActivity(Date.now());
    const events = ['click', 'keydown', 'touchstart', 'scroll'];
    events.forEach(e => window.addEventListener(e, resetActivity));

    const interval = setInterval(() => {
      if (Date.now() - lastActivity > 2 * 60 * 60 * 1000) {
        handleLogout();
      }
    }, 60000);

    return () => {
      events.forEach(e => window.removeEventListener(e, resetActivity));
      clearInterval(interval);
    };
  }, [user, lastActivity, handleLogout]);

  return (
    <AuthContext.Provider value={{ user, loading, login: handleLogin, logout: handleLogout }}>
      {children}
    </AuthContext.Provider>
  );
}

function ProtectedRoute({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex items-center justify-center h-screen"><div className="animate-spin h-8 w-8 border-4 border-[#1a2fce] border-t-transparent rounded-full" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function OfflineBanner() {
  const { isOnline, queueLength, syncing, syncQueue } = useOffline();

  useEffect(() => {
    if (isOnline && queueLength > 0) {
      syncQueue().then((count) => {
        if (count > 0) {
          sonnerToast.success(`Synced ${count} queued change${count > 1 ? 's' : ''}`);
        }
      });
    }
  }, [isOnline, queueLength, syncQueue]);

  if (isOnline && queueLength === 0) return null;

  return (
    <div className={`fixed top-14 left-0 right-0 z-[60] px-4 py-2 text-sm font-medium text-center ${
      isOnline ? 'bg-green-500 text-white' : 'bg-amber-500 text-white'
    }`} data-testid="offline-banner">
      {!isOnline ? (
        <>Working offline — {queueLength} change{queueLength !== 1 ? 's' : ''} queued</>
      ) : syncing ? (
        <>Syncing {queueLength} queued changes...</>
      ) : (
        <>{queueLength} changes ready to sync</>
      )}
    </div>
  );
}

// Register service worker — auto-update on new deploys
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').then((reg) => {
      // Periodically check for SW updates while the tab is open
      setInterval(() => { reg.update().catch(() => {}); }, 60 * 1000);

      // When a new SW is installed and waiting, ask it to take over immediately
      const promote = (worker) => {
        if (!worker) return;
        worker.addEventListener('statechange', () => {
          if (worker.state === 'installed' && navigator.serviceWorker.controller) {
            worker.postMessage({ type: 'SKIP_WAITING' });
          }
        });
      };
      promote(reg.waiting);
      reg.addEventListener('updatefound', () => promote(reg.installing));
    }).catch(() => {});

    // Reload once when a new SW takes control so the user gets the latest assets
    let reloaded = false;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (reloaded) return;
      reloaded = true;
      window.location.reload();
    });
  });
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <OfflineBanner />
        <Toaster position="top-right" richColors />
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
            <Route index element={<DashboardPage />} />
            <Route path="inventory" element={<InventoryPage />} />
            <Route path="pack" element={<PackPage />} />
            <Route path="pack/:palletId" element={<PackPage />} />
            <Route path="scan" element={<ScanPage />} />
            <Route path="pallet/:palletId" element={<PalletDetailPage />} />
            <Route path="map" element={<WarehouseMapPage />} />
            <Route path="issue/:palletId" element={<IssuePage />} />
            <Route path="customers" element={<CustomersPage />} />
            <Route path="grn" element={<GRNPage />} />
            <Route path="labels" element={<LabelsPage />} />
            <Route path="cycle-count" element={<CycleCountPage />} />
            <Route path="cycle-count/:countId" element={<CycleCountPage />} />
            <Route path="returns" element={<ReturnsPage />} />
            <Route path="tool-store" element={<ProtectedRoute roles={['admin','manager']}><ToolStorePage /></ProtectedRoute>} />
            <Route path="tool-store/:toolId" element={<ProtectedRoute roles={['admin','manager']}><ToolDetailPage /></ProtectedRoute>} />
            <Route path="reports" element={<ReportsPage />} />
            <Route path="admin" element={<ProtectedRoute roles={['admin']}><AdminPage /></ProtectedRoute>} />
          </Route>
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
